import React, { useEffect, useContext, Fragment, useState, useCallback } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { FaCaretDown, FaGlobeAsia, FaUserCircle } from "react-icons/fa"
import { s3Static } from "../../utils/constants"
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import { LocationContext } from '../../context/LocationContext'
import { useRouter } from 'next/router'
import { getAppSettings } from '../../api/app-setting/app-setting'
import { MyLink } from './MyLink'
import ReactCountryFlag from 'react-country-flag'
import { AirconNavBar } from './navbars/AirconNavBar'
import { AirconNavBarSmall } from './navbars/AirconNavBarSmall'
import { HandymanNavBar, HandymanNavBarSmall } from './navbars/HandymanNavBar'
import { HelperNavBarSmall } from './navbars/HelperNavBarSmall'
import { HelperNavBar } from './navbars/HelperNavBar'
import { TrainerNavBarSmall } from './navbars/TrainerNavBarSmall'
import { TrainerNavBar } from './navbars/TrainerNavBar'
import { NavDropdown, NavLinkItem, SegmentNavBar } from './navbars/NavDropdown'
import { SegmentNavBarSmall } from './navbars/SegmentNavBarSmall'
import { ABOUT_PATH, COUNTRY_SWITCHER, aboutPath, getMarketFromPath, getNavHubLinks, marketBlogsPath, rememberMarket } from '../../utils/markets'
import { getMarketSegmentMenu } from '../../utils/market-nav'

// Singapore hubs. Absolute /sg paths: these used to be built as
// `/${countryCd}/${path}`, which sent non-SG visitors to e.g. /us/aircon-servicing-singapore (404).
const serviceHubLinks = [
    { href: "/sg/aircon-servicing-singapore", label: "Aircon Servicing Singapore" },
    { href: "/sg/personal-trainer", label: "Personal Trainer Singapore" },
    { href: "/sg/handyman-singapore-services", label: "Handyman Services" },
    { href: "/sg/electrician-services-singapore", label: "Electrician Services" },
    { href: "/sg/part-time-cleaner-singapore", label: "Part Time Helper" },
]

export const IndexNav = () => {
    const router = useRouter()

    const routeCountryCd = router.query.countryCd

    const routeLocality = router.query.locality

    const [ waInd, setWaInd ] = useState(false)

    const { countryCd: contextCountryCd, locality, countryName } = useContext(LocationContext)

    // On /ae and /my pages the route decides the country, so the server render
    // (where LocationContext is still the SG default) already shows the right
    // flag and menu instead of flashing Singapore's.
    const routeMarket = getMarketFromPath(router.pathname)
    const countryCd = routeMarket ? routeMarket.countryCd : contextCountryCd
    const marketHubLinks = getNavHubLinks(countryCd)
    const hubLinks = marketHubLinks ?? serviceHubLinks
    // UAE / Malaysia aircon and trainer pages: the city's segment menu, the
    // counterpart of AirconNavBar / TrainerNavBar on the Singapore pages.
    const marketSegment = getMarketSegmentMenu(router.pathname)
    // The segment menu links the blog listing itself (Guides > All guides).
    const hasBlogs = !marketSegment
    const blogsHref = marketBlogsPath(countryCd) ?? `/${countryCd}/blogs`
    // One company About page; the UAE and Malaysia jump to their section.
    const aboutHref = aboutPath(countryCd)
    // "/" is the global page: no country of its own, so the switcher shows a
    // globe instead of the LocationContext default's (Singapore) flag.
    const isGlobalHome = router.pathname === '/'

    // Remember the country being browsed for the global page's country cards.
    useEffect(() => {
        if (routeMarket) rememberMarket(routeMarket.countryCd)
    }, [routeMarket])

    useEffect(() => {
        window.onscroll =  () => {
            const header = document.getElementById("header");
            if(header == undefined) return
            const height = document.documentElement.scrollTop
            if(height > 30) {            
                header.classList.remove("nav-style-transparent")
                header.classList.add("nav-style")
            } else {
                header.classList.add("nav-style-transparent")
                header.classList.remove("nav-style")
            }
        };
            
    }, [])

    const fetchAppSetting = useCallback(async() => {
        try {
            const appSettings = await getAppSettings(countryName)
            setWaInd(appSettings.waBusinessInd)
        } catch (error) {
            console.log(error);
        }
    }, [countryName])
    
    useEffect(() => {
        if(!countryName) return

        fetchAppSetting()
    }, [countryName, fetchAppSetting])
    
  return (
    <header id="header" className='h-14 w-full block fixed top-0 right-0 left-0 grid-flow-row nav-style-transparent z-20 transition-all duration-200'>    
        <div id="navbar" className="max-w-7xl mx-auto pl-2 sm:px-6 xl:px-4 relative h-14 flex justify-between items-center">
            <div className='flex items-center space-x-6 md:space-x-10'>
                <Link href={routeMarket ? routeMarket.homePath : '/'} prefetch={false} className='flex items-center'>
                    <Image 
                        height={47} 
                        width={60} 
                        src={s3Static + "logo.png"} 
                        alt="StringsSG Logo"
                        style={{'height': '47px', 'width': '60px'}}
                    />
                </Link> 
                <div  id='country-selection' className='flex justify-center space-x-10' role="navigation" aria-label="Country selection">
                    <Menu as="div" className="relative">
                        <MenuButton className="focus:outline-none" aria-label="Select country">
                            <div className='flex items-center py-2 text-orange-500 space-x-1'>
                                {isGlobalHome ? (
                                    <FaGlobeAsia className='h-[1.8em] w-[1.8em]' aria-hidden="true" />
                                ) : (
                                <ReactCountryFlag className='shadow-md'
                                    countryCode={countryCd}
                                    svg
                                    style={{
                                        width: '1.8em',
                                        height: '1.8em',
                                    }}
                                    title={countryCd}
                                />
                                )}
                                <FaCaretDown className="h-5 w-5 " aria-hidden="true" />
                            </div>
                        </MenuButton>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="transform opacity-0 scale-y-0"
                            enterTo="transform opacity-100 scale-y-100"
                            leave="transition ease-in duration-200"
                            leaveFrom="transform opacity-100 scale-y-100"
                            leaveTo="transform opacity-0 scale-y-0"
                            >
                                <MenuItems as="ul" className="absolute space-y-2 p-2 divide-y text-center -left-3 m-auto w-48 origin-top-right bg-white divide-gray-100 rounded-md shadow-lg ring-1 ring-black/5 focus:outline-none">
                                    {COUNTRY_SWITCHER.map((country) => (
                                        <MenuItem key={country.countryCd}> 
                                            <MyLink href={country.href} prefetch={false} className="flex items-center text-amber-600 p-2" aria-label={`Switch to ${country.label}`}>
                                                    <ReactCountryFlag className='shadow-md'
                                                        countryCode={country.countryCd.toUpperCase()}
                                                        svg
                                                        style={{
                                                            width: '1.3em',
                                                            height: '1.3em',
                                                        }}
                                                        title={country.countryCd.toUpperCase()}
                                                    />
                                                    <span className='px-2 text-left'>{country.label}</span>
                                            </MyLink>
                                        </MenuItem>
                                    ))}
                                </MenuItems>
                        </Transition>
                    </Menu>
                </div>
            </div>
            <nav className='flex space-x-4'>                        
                <div className="xl:hidden right-0 flex items-center space-x-4">
                    <div>
                        <Link href="/signin" prefetch={false} 
                            className="flex justify-center w-full outline-none text-4xl bg-transparent text-orange-500 hover:text-orange-400"
                            aria-label="Sign in to your account"
                        >
                            <FaUserCircle aria-hidden="true"/>
                        </Link>
                    </div>
                    <Menu as="div" className="relative">
                        <MenuButton aria-label="Mobile menu" className="inline-flex items-center justify-center p-2 rounded-md bg-transparent text-orange-400 ring ring-orange-400 hover:bg-orange-400 hover:text-white focus:bg-orange-400 focus:text-white outline-none focus:ring-2 focus:ring-inset focus:ring-white focus:outline-none" 
                            aria-controls="mobile-menu" aria-expanded="false">    
                            <span className="sr-only">Open main menu</span>
                            <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                            <svg className="hidden h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </MenuButton>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="transform opacity-0 scale-y-0"
                            enterTo="transform opacity-100 scale-y-100"
                            leave="transition ease-in duration-200"
                            leaveFrom="transform opacity-100 scale-y-100"
                            leaveTo="transform opacity-0 scale-y-0"
                            >
                            <nav>
                                <MenuItems as="ul" className="absolute space-y-3 px-2 py-3 divide-y text-center right-0 w-72 mt-1 origin-top-right bg-white divide-gray-100 rounded-md shadow-lg ring-1 ring-black/5 focus:outline-none"
                                    aria-label="Mobile navigation menu"
                                    role="menu"
                                >
                                    <MenuItem> 
                                        <MyLink href={aboutHref} prefetch={false} className="flex justify-center px-2 py-2 text-orange-600"
                                        aria-current={router.pathname === ABOUT_PATH ? "page" : undefined}>
                                            About Us                       
                                        </MyLink>
                                    </MenuItem>
                                    {/* UAE / Malaysia: on a service page the hubs fold into a
                                        Services dropdown ahead of the city's segment menu;
                                        elsewhere (the /ae and /my home pages) they are listed. */}
                                    {marketSegment && (
                                        <SegmentNavBarSmall items={[
                                            { type: 'dropdown', label: 'Services', items: marketHubLinks ?? [] },
                                            ...marketSegment.items,
                                        ]} />
                                    )}
                                    {marketHubLinks && !marketSegment && marketHubLinks.map((link) => (
                                        <MenuItem key={link.href}>
                                            <MyLink href={link.href} prefetch={false} className="flex justify-center px-2 py-2 text-orange-600"
                                                aria-current={router.asPath.split('?')[0] === link.href ? "page" : undefined}>
                                                {link.label}
                                            </MyLink>
                                        </MenuItem>
                                    ))}
                                    {router.pathname.includes("/sg/personal-trainer") &&
                                        <TrainerNavBarSmall waInd={waInd}/>
                                    }
                                    {router.pathname.includes("/sg/part-time-cleaner-singapore") &&
                                        <HelperNavBarSmall/>
                                    }
                                    {router.pathname.includes("/sg/aircon-servicing-singapore") &&
                                        <AirconNavBarSmall waInd={waInd}/>
                                    }
                                    {/* Handyman, plumber and electrician sections: dropdowns, pricing,
                                        blogs and the WhatsApp number, like the aircon section. */}
                                    { router.pathname.includes("/sg/handyman-singapore-services") &&
                                        <HandymanNavBarSmall section="handyman"/>
                                    }
                                    { router.pathname.includes("/sg/plumber-singapore-services") &&
                                        <HandymanNavBarSmall section="plumber"/>
                                    }
                                    { router.pathname.includes("/sg/electrician-services-singapore") &&
                                        <HandymanNavBarSmall section="electrician"/>
                                    }
                                    {(!router.pathname.includes("/sg/aircon-servicing-singapore") 
                                        && !router.pathname.includes("/sg/part-time-cleaner-singapore")
                                        && !router.pathname.includes("/sg/handyman-singapore-services")
                                        && !router.pathname.includes("/sg/plumber-singapore-services")
                                        && !router.pathname.includes("/sg/electrician-services-singapore")
                                        && !router.pathname.includes("/sg/personal-trainer")
                                        && hasBlogs
                                    ) &&  
                                        <MenuItem>
                                            <MyLink href={blogsHref}
                                                    prefetch={false} className="flex justify-center px-2 py-2 text-orange-600" 
                                                    aria-label="General Blogs">
                                                Blogs                       
                                            </MyLink>            
                                        </MenuItem>  
                                    }
                                </MenuItems>
                            </nav>
                        </Transition>
                    </Menu>
                </div>
                {/* Destop Navigation */}
                <div className="hidden xl:flex space-x-4 items-center">
                    <ul className="flex items-center space-x-4 text-sm">
                        {/* Every desktop item comes from navbars/NavDropdown, so all
                            dropdowns open the same way (hover, click or focus) and keep
                            their links in the static HTML crawlers see. Menu labels must
                            stay unique: "Services" is this site-wide menu, so segment
                            menus are named for their segment. */}
                        <NavDropdown
                            label="Services"
                            items={hubLinks}
                        />
                        <NavLinkItem href={aboutHref} label="About Us" current={router.pathname === ABOUT_PATH} />
                        {marketSegment && <SegmentNavBar items={marketSegment.items} />}
                        {router.pathname.includes("/sg/personal-trainer") &&
                            <TrainerNavBar/>
                        }
                        {router.pathname.includes("/sg/part-time-cleaner-singapore") &&
                            <HelperNavBar/>
                        }
                        {router.pathname.includes("/sg/aircon-servicing-singapore") &&
                            <AirconNavBar/>
                        }
                        {router.pathname.includes("/sg/handyman-singapore-services") &&
                            <HandymanNavBar section="handyman"/>
                        }
                        {router.pathname.includes("/sg/plumber-singapore-services") &&
                            <HandymanNavBar section="plumber"/>
                        }
                        {router.pathname.includes("/sg/electrician-services-singapore") &&
                            <HandymanNavBar section="electrician"/>
                        }
                        {(!router.pathname.includes("/sg/aircon-servicing-singapore")
                            && !router.pathname.includes("/sg/part-time-cleaner-singapore")
                            && !router.pathname.includes("/sg/handyman-singapore-services")
                            && !router.pathname.includes("/sg/plumber-singapore-services")
                            && !router.pathname.includes("/sg/electrician-services-singapore")
                            && !router.pathname.includes("/sg/personal-trainer")
                            && hasBlogs
                        ) &&
                            <NavLinkItem href={blogsHref} label="Blogs" />
                        }
                        <li>
                            <Link href="/signin" prefetch={false} className="flex justify-center w-full px-4 py-2 bg-linear-to-t from-orange-600 to-amber-400 text-white hover:from-orange-500 hover:to-amber-400 focus:from-orange-500 focus:to-amber-400 rounded-full drop-shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-amber-400 ring-amber-200"
                                aria-label="Sign in to your account">                                        
                                <strong>Sign In</strong>
                            </Link>
                        </li>
                    </ul>
                </div>
            </nav>
        </div>
    </header>
  )
}

